import React, { useState, useEffect, useMemo, useRef } from "react";
import { Row, Col, Card, Button, Form, Badge, Modal } from "react-bootstrap";
import { getBroadcasts, showDate, getBroadcastContacts, getBroadcastContactStatus } from "../../../helpers/api";
import Table from "../../../components/Table";
import ShreyuDatepicker from "../../../components/Datepicker";
import { Search, Eye, PencilSquare, Trash, Check2, Check2All, Reply, Send, XCircle } from 'react-bootstrap-icons';
import FeatherIcon from 'feather-icons-react';

const RECIPIENTS_PAGE_SIZE = 50;

export interface Broadcast {
    br_id: string;
    broadcast_name: string;
    template_name: string;
    date: string;
    contact_count: number;
    created_at: string;
    sent_count: string,
    delivered_count: string,
    read_count: string,
    failed_count: string
    sending_count?: string,
    replied_count?: string,
    status: "scheduled" | "completed" | "failed" | "pending";
}

const BroadcastList: React.FC = () => {
    const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
    const [filtered, setFiltered] = useState<Broadcast[]>([]);
    const [loading, setLoading] = useState(true);

    const [showContactsModal, setShowContactsModal] = useState(false);
    const [contactStatusList, setContactStatusList] = useState<any[]>([]);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientsPage, setRecipientsPage] = useState(1);
    const [recipientsLastPage, setRecipientsLastPage] = useState(1);
    const [recipientsTotal, setRecipientsTotal] = useState(0);
    const recipientsRequest = useRef(0);
    const recipientsTarget = useRef<{ broadcastId: string; status?: string } | null>(null);

    const [scheduleDate, setScheduleDate] = useState<Date | undefined>(new Date());
    const [endDate, setEndDate] = useState<string>(
        new Date().toISOString().split("T")[0]   // default today
    );
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedBroadcast, setSelectedBroadcast] = useState<Broadcast | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

    const [messageCounts, setMessageCounts] = useState({
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0
    });

    useEffect(() => {
        const loadInitial = async () => {
            setLoading(true);
            const from_date = (scheduleDate ?? new Date())
                .toISOString()
                .split("T")[0];
            const to_date = endDate;

            try {
                const response = await showDate(from_date, to_date);
                const rows = response?.data?.data ?? [];
                setBroadcasts(rows);
                setFiltered(rows);
            } catch (error) {
                console.error(error);
                setFiltered([]);
            } finally {
                setLoading(false);
            }
        };

        loadInitial();
    }, []);

    const applyFilters = async () => {
        if (!scheduleDate) {
            alert("Please select start date");
            return;
        }

        const from_date = scheduleDate.toISOString().split("T")[0];
        const to_date = endDate;

        setLoading(true);

        try {
            const response = await showDate(from_date, to_date);
            const rows = response?.data?.data ?? [];

            if (rows.length === 0) {
                // alert("No broadcasts found for selected date range");
                setFiltered([]);
                return;
            }

            setFiltered(rows);
        } catch (error) {
            console.error(error);
            alert("Something went wrong while fetching broadcasts.");
            setFiltered([]);
        } finally {
            setLoading(false);
        }
    };


    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString("en-GB").replace(/\//g, "-");
    };

    const getStatusVariant = (status: Broadcast["status"]) => {
        switch (status) {
            case "completed": return "success";
            case "scheduled": return "warning";
            case "pending": return "warning";
            case "failed": return "danger";
            default: return "secondary";
        }
    };

    const handleDelete = (b: Broadcast) => {
        setSelectedBroadcast(b);
        setShowDeleteModal(true);
    };

    // Recipients are loaded one page at a time; counts are computed by the server.
    const loadRecipients = async (broadcastId: string, status: string | undefined, page: number) => {
        const id = ++recipientsRequest.current;
        setRecipientsLoading(true);
        try {
            const params = { page, per_page: RECIPIENTS_PAGE_SIZE };
            const response = status
                ? await getBroadcastContactStatus(broadcastId, status, params)
                : await getBroadcastContacts(broadcastId, params);
            if (id !== recipientsRequest.current) return;

            setContactStatusList(response.data?.contacts ?? []);
            setRecipientsPage(response.data?.meta?.current_page ?? page);
            setRecipientsLastPage(response.data?.meta?.last_page ?? 1);
            setRecipientsTotal(response.data?.meta?.total ?? 0);
            if (response.data?.counts) {
                setMessageCounts({
                    sent: response.data.counts.sent ?? 0,
                    delivered: response.data.counts.delivered ?? 0,
                    read: response.data.counts.read ?? 0,
                    failed: response.data.counts.failed ?? 0,
                });
            }
        } finally {
            if (id === recipientsRequest.current) setRecipientsLoading(false);
        }
    };

    const handleOpenContacts = async (broadcast: Broadcast) => {
        setShowContactsModal(true);
        setContactStatusList([]);
        recipientsTarget.current = { broadcastId: broadcast.br_id, status: undefined };
        await loadRecipients(broadcast.br_id, undefined, 1);
    };
    const handleOpenContactsstatus = async (
        broadcast: Broadcast,
        status?: string
    ) => {
        setShowContactsModal(true);
        setContactStatusList([]);
        recipientsTarget.current = { broadcastId: broadcast.br_id, status };
        await loadRecipients(broadcast.br_id, status, 1);
    };
    const goToRecipientsPage = (page: number) => {
        const target = recipientsTarget.current;
        if (target) loadRecipients(target.broadcastId, target.status, page);
    };

    const filteredContacts = useMemo(() => {
        if (!selectedStatus) return contactStatusList;
        return contactStatusList.filter(
            (c: any) => c.message_status === selectedStatus
        );
    }, [contactStatusList, selectedStatus]);

    // Aggregate message metrics across the broadcasts in view for the overview cards.
    const overview = useMemo(() => {
        const sum = (key: keyof Broadcast) =>
            filtered.reduce((acc, b) => acc + (Number(b[key]) || 0), 0);
        return {
            sent: sum("sent_count"),
            delivered: sum("delivered_count"),
            read: sum("read_count"),
            replied: sum("replied_count"),
            sending: sum("sending_count"),
            failed: sum("failed_count"),
        };
    }, [filtered]);

    const overviewCards = [
        { key: "sent", label: "Sent", value: overview.sent, icon: <Check2 size={16} />, bg: "#e9f7ef", color: "#2e9e5b" },
        { key: "delivered", label: "Delivered", value: overview.delivered, icon: <Check2All size={16} />, bg: "#e7f1ff", color: "#2f6fed" },
        { key: "read", label: "Read", value: overview.read, icon: <Eye size={16} />, bg: "#eafaf1", color: "#12b76a" },
        { key: "replied", label: "Replied", value: overview.replied, icon: <Reply size={16} />, bg: "#e8f8f5", color: "#0ca789" },
        { key: "sending", label: "Sending", value: overview.sending, icon: <Send size={16} />, bg: "#fff4e5", color: "#f79009" },
        { key: "failed", label: "Failed", value: overview.failed, icon: <XCircle size={16} />, bg: "#fdecec", color: "#e5484d" },
    ];

    const columns = useMemo(
        () => [
            {
                Header: "S No",
                accessor: "serial_no", // FIXED
                Cell: ({ row }: any) => row.index + 1
            },
            {
                Header: "Date",
                accessor: "date",
                Cell: ({ value }: any) => formatDate(value)
            },
            {
                Header: "Broadcast Name",
                accessor: "broadcast_name"
            },
            {
                Header: "Template",
                accessor: "template_name"
            },
            {
                Header: "Contacts",
                accessor: "contact_count",
                Cell: ({ row }: any) => (
                    <Badge
                        bg="primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleOpenContacts(row.original)}
                    >
                        {row.original.contact_count}
                    </Badge>
                )
            },
            {
                Header: "Message Status",
                accessor: "stats",
                Cell: ({ row }: any) => {
                    const b = row.original;
                    return (
                        <div className="mb-3 d-flex gap-2 flex-wrap">
                            <Badge
                                bg="warning"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "sent")}
                            >
                                Sent {b.sent_count}
                            </Badge>

                            <Badge
                                bg="info"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "delivered")}
                            >
                                Delivered {b.delivered_count}
                            </Badge>

                            <Badge
                                bg="success"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "read")}
                            >
                                Read {b.read_count}
                            </Badge>

                            <Badge
                                bg="danger"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "failed")}
                            >
                                Failed {b.failed_count}
                            </Badge>

                        </div>


                    );
                }
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }: any) => (
                    <Badge bg={getStatusVariant(value)}>{value}</Badge>
                )
            },
            {
                Header: "Actions",
                accessor: "actions", // FIXED
                Cell: ({ row }: any) => (
                    <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(row.original)}
                    >
                        <Trash />
                    </Button>
                )
            }
        ],
        []
    );


    return (
        <>
            {/* BROADCAST ANALYTICS OVERVIEW */}
            <Card className="mb-3">
                <Card.Body>
                    <h5 className="mb-1">Broadcast analytics</h5>
                    <div className="text-muted mb-3" style={{ fontSize: 13 }}>Overview</div>
                    <Row className="g-2">
                        {overviewCards.map((s) => (
                            <Col key={s.key} xs={6} sm={4} lg={2}>
                                <div
                                    className="d-flex align-items-center justify-content-between h-100"
                                    style={{
                                        padding: "14px 16px",
                                        background: "#f8f9fb",
                                        borderRadius: 12,
                                        border: "1px solid #eef0f4",
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, color: "#1b1f27" }}>
                                            {s.value.toLocaleString()}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: 13 }}>{s.label}</div>
                                    </div>
                                    <span
                                        className="d-flex align-items-center justify-content-center"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            background: s.bg,
                                            color: s.color,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {s.icon}
                                    </span>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                {/* <Card.Header className="bg-secondary text-white"> */}
                {/* <h5 className="mb-0">Broadcast List</h5> */}
                {/* </Card.Header> */}

                <Card.Body>
                    <Row className="align-items-end g-3">
                        <Col md={2} sm={12}>

                        </Col>
                        {/* START DATE */}
                        <Col md={3} sm={12} >
                            <Form.Group className="me-0">
                                <Form.Label>Start Date</Form.Label>
                                <ShreyuDatepicker
                                    hideAddon
                                    // dateFormat="yyyy-MM-dd"
                                    dateFormat="dd-MM-yyyy"
                                    value={scheduleDate ? scheduleDate : new Date()}
                                    onChange={(date: Date) => setScheduleDate(date)}
                                />
                            </Form.Group>
                        </Col>

                        {/* END DATE */}
                        <Col md={3} sm={12} >
                            <Form.Group className="ms-0">
                                <Form.Label>End Date</Form.Label>
                                <ShreyuDatepicker
                                    hideAddon
                                    dateFormat="dd-MM-yyyy"
                                    value={new Date(endDate)}
                                    onChange={(date: Date) =>
                                        setEndDate(date.toISOString().split("T")[0])
                                    }
                                />
                            </Form.Group>
                        </Col>

                        {/* BUTTON */}
                        <Col md={2} sm={12}>
                            <Button
                                variant="primary"
                                onClick={applyFilters}
                            >
                                <i className="mdi mdi-magnify"></i> <Search />
                            </Button>
                        </Col>
                        <Col md={2} sm={12}>

                        </Col>
                    </Row>
                </Card.Body>


            </Card>

            <Card>
                <Card.Body>
                    <Badge bg="info" className="mb-3">
                        {filtered.length} broadcast(s) found
                    </Badge>

                    <Table
                        columns={columns}
                        data={filtered}
                        pageSize={10}
                        isSearchable
                        pagination
                    />
                </Card.Body>
            </Card>

            {/* CONTACT STATUS MODAL */}
            <Modal
                show={showContactsModal}
                onHide={() => setShowContactsModal(false)}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Message Status Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3 d-flex flex-wrap gap-2">
                        <Badge bg="warning">Sent: {messageCounts.sent.toLocaleString()}</Badge>
                        <Badge bg="info">Delivered: {messageCounts.delivered.toLocaleString()}</Badge>
                        <Badge bg="success">Read: {messageCounts.read.toLocaleString()}</Badge>
                        <Badge bg="danger">Failed: {messageCounts.failed.toLocaleString()}</Badge>
                    </div>

                    <div style={{ opacity: recipientsLoading && contactStatusList.length ? 0.55 : 1, transition: "opacity .15s", minHeight: 160 }}>
                        <table className="table table-striped table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>S No</th>
                                    <th>Date</th>
                                    <th>Contact No</th>
                                    <th>Name</th>
                                    <th>Message Status</th>
                                    <th>Error Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contactStatusList.map((row: any, i: number) => {
                                    const status = (row.message_status || "").toLowerCase();
                                    const bg =
                                        status === "sent" ? "warning"
                                        : status === "failed" ? "danger"
                                        : status === "delivered" ? "info"
                                        : status === "read" ? "success"
                                        : "secondary";
                                    return (
                                        <tr key={`${(recipientsPage - 1) * RECIPIENTS_PAGE_SIZE + i}`}>
                                            <td>{(recipientsPage - 1) * RECIPIENTS_PAGE_SIZE + i + 1}</td>
                                            <td>{row.created_date_time ? formatDate(row.created_date_time) : ""}</td>
                                            <td>{row.contact_no}</td>
                                            <td>{row.contact_name}</td>
                                            <td><Badge bg={bg}>{row.message_status}</Badge></td>
                                            <td>{status === "failed" ? row.error || "-" : "-"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {!recipientsLoading && contactStatusList.length === 0 && (
                            <div className="text-center text-muted py-4">No recipients found</div>
                        )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-muted">
                            {recipientsLoading ? "Loading…" : `${recipientsTotal.toLocaleString()} recipients`}
                        </small>
                        {recipientsLastPage > 1 && (
                            <div className="d-flex align-items-center gap-2">
                                <Button size="sm" variant="outline-secondary" disabled={recipientsPage === 1 || recipientsLoading} onClick={() => goToRecipientsPage(recipientsPage - 1)}>
                                    Prev
                                </Button>
                                <small>
                                    Page {recipientsPage.toLocaleString()} of {recipientsLastPage.toLocaleString()}
                                </small>
                                <Button size="sm" variant="outline-secondary" disabled={recipientsPage === recipientsLastPage || recipientsLoading} onClick={() => goToRecipientsPage(recipientsPage + 1)}>
                                    Next
                                </Button>
                            </div>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowContactsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BroadcastList;
